import { ISubTaskSwagUpChoiceTemplate } from '@dadohr/shared/domainTypes/experience';
import { SwagUpInventoryItem } from '@dadohr/shared/domainTypes/integrations/swagUp';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { Chip, FormControlLabel, Switch } from '@material-ui/core';
import { SetStateAction } from 'react';
import {
  AutocompleteMulti,
  AutocompleteMultiFreeSolo
} from '../../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { callFirebaseFunction } from '../../../../../../../../services/firebaseFunctions';
import { getUsersByOrgId } from '../../../../../../../../services/user';
import { TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  VALIDATORS_SWAG_UP_CHOICE,
  useFieldValidator
} from '../../../validation';

const getActiveInventory = (orgId: string) => {
  return callFirebaseFunction<SwagUpInventoryItem[]>(
    'integrations_swagUp-getActiveInventory',
    {
      orgId
    }
  ).catch((err) => {
    console.log('SWAG_UP_ERROR', err);
    return [];
  });
};

export const BlockSettingsSwagUpChoice = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl
}: {
  value: ISubTaskSwagUpChoiceTemplate;
  onChange: (nextValue: ISubTaskSwagUpChoiceTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();

  const validateChoices = useFieldValidator(
    setValidation,
    VALIDATORS_SWAG_UP_CHOICE,
    'choices'
  );
  const invalidChoicesReason = validation?.fields.choices || null;

  const validateContacts = useFieldValidator(
    setValidation,
    VALIDATORS_SWAG_UP_CHOICE,
    'contacts'
  );
  const invalidContactsReason = validation?.fields.contacts || null;
  return (
    <>
      <FormRow>
        <AutocompleteMulti
          label={t('Swag to choose')}
          fullWidth
          value={value.data.choices}
          options={() =>
            getActiveInventory(expTpl.orgId).then((xs) =>
              xs.map((x) => ({
                product: x.id,
                product_name: x.name,
                product_image: x.image,
                sizes: x.stock.map((s) => s.size)
              }))
            )
          }
          getOptionSelected={(o, v) => o.product === v.product}
          getOptionLabel={(o) => o.product_name}
          onChange={(choices) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                choices
              }
            };
            invalidChoicesReason && validateChoices(nextValue, expTpl);
            return onChange(nextValue);
          }}
          error={!!invalidChoicesReason}
          helperText={invalidChoicesReason}
          renderOption={(o) => o.product_name}
          renderTags={(os, getTagProps) =>
            os.map((o, index) => (
              <Chip
                variant="default"
                size="small"
                label={o.product_name}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </FormRow>
      <FormRow>
        <FormControlLabel
          label={t('Show swag photos')}
          control={
            <Switch
              checked={!value.data.opts.hidePhotos}
              onChange={(ev) => {
                onChange({
                  ...value,
                  data: {
                    ...value.data,
                    opts: {
                      ...value.data.opts,
                      hidePhotos: !ev.target.checked
                    }
                  }
                });
              }}
              color="primary"
            />
          }
        />
      </FormRow>

      <FormRow>
        <AutocompleteMultiFreeSolo
          label={t('SwagUp Contacts')}
          fullWidth
          value={value.data.swagUpContactEmails}
          error={!!invalidContactsReason}
          helperText={invalidContactsReason}
          onChange={(nextEmails) => {
            const nextValue = {
              ...value,
              data: {
                ...value.data,
                swagUpContactEmails: nextEmails
              }
            };
            validateContacts(nextValue, expTpl);
            onChange(nextValue);
          }}
          options={() =>
            getUsersByOrgId(expTpl.orgId).then((us) =>
              us.map((u) => u.data.email).filter((t) => t)
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="default"
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </FormRow>
    </>
  );
};

export const BlockSettingsSwagUpChoiceReadOnly = ({
  value
}: {
  value: ISubTaskSwagUpChoiceTemplate;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <FormRow>
        <DataFieldWithLabel label={t('Swag to choose')}>
          {value.data.choices.map((c) => c.product_name).join(' • ')}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        {value.data.opts.hidePhotos
          ? t('Hide swag photos')
          : t('Show swag photos')}
      </FormRow>
    </>
  );
};
